import React from "react";
import Icon from "./Icon";
import { useI18n } from "../i18n";
import { C, R, S } from "../theme";

export const TERMS_TITLE = "SafeBook — Terms of Service & Legal Disclaimer";
export const TERMS_VERSION = "1.0";

const SECTIONS = [
  {
    h: "1. Digital Record-Keeping Tool Only",
    p: ["SafeBook is provided strictly as an administrative digital logging tool to assist showmen, operators, and site managers with record management. SafeBook is NOT a safety inspection agency, engineering authority, or regulatory body."],
  },
  {
    h: "2. Operator's Primary Duty of Care & Responsibility",
    p: ['The duty holder, site manager, and ride operator ("The User") retain 100% legal, statutory, and operational responsibility for:'],
    list: [
      "Ensuring all rides, inflatables, and equipment are safe, properly maintained, and compliant with all relevant laws, statutory requirements, and guidelines (including HSE, HSG175, PUWER, ADIPS, PIPA, and EN14960).",
      "Conducting thorough, physical daily checks, wind monitoring, maintenance, and staff training.",
      "Ensuring that all information entered into SafeBook is true, accurate, and completed by qualified personnel.",
    ],
  },
  {
    h: "3. Equipment Uniqueness & Manufacturer Specifications",
    p: ["Pre-populated checklists, templates, wind limits, or guidance provided within SafeBook are generic reference guides only. Every piece of equipment is unique. The User must inspect and maintain each ride strictly according to its specific manufacturer manual, engineering assessments, and current regulatory standards. SafeBook assumes no responsibility for generic checklist suitability."],
  },
  {
    h: "4. Exclusion of Liability",
    p: ["To the fullest extent permitted by law, SafeBook (and its parent company, directors, employees, and software providers) shall NOT be liable for any:"],
    list: [
      "Personal injury, loss of life, property damage, or financial loss resulting from ride operation, structural failure, or site accidents.",
      "Failure of the User or its staff to conduct physical inspections correctly or act upon flagged defects or high wind readings.",
      "Loss, corruption, or inaccuracy of data entered into or generated by the app.",
    ],
  },
  {
    h: "5. Indemnification",
    p: ["The User agrees to indemnify, defend, and hold harmless SafeBook against any legal claims, liabilities, damages, costs, or legal fees arising from the User's operation of equipment, non-compliance with health & safety laws, or reliance on this app as an absolute safety guarantee."],
  },
];

export function TermsContent() {
  return (
    <div data-testid="terms-content">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, marginBottom: S.md }}>
        <Icon name="shield-checkmark" size={22} color={C.amber} />
        <div className="display" style={{ color: C.text, fontSize: 18, fontWeight: 900, letterSpacing: 0.5 }}>SAFEBOOK — TERMS OF SERVICE & LEGAL DISCLAIMER</div>
      </div>
      {SECTIONS.map((s) => (
        <div key={s.h} style={{ marginBottom: S.lg }}>
          <div style={{ color: C.amber, fontSize: 15, fontWeight: 800, marginBottom: S.xs }}>{s.h}</div>
          {(s.p || []).map((para, i) => (
            <p key={i} style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, margin: `0 0 ${S.sm}px` }}>{para}</p>
          ))}
          {s.list && (
            <ul style={{ margin: `${S.xs}px 0 0`, paddingLeft: 20 }}>
              {s.list.map((li, i) => (
                <li key={i} style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginBottom: 6 }}>{li}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, borderTop: `1px solid ${C.border}`, paddingTop: S.md, marginTop: S.sm }}>
        By using SafeBook you acknowledge that you have read, understood, and agree to these Terms of Service and Legal Disclaimer in full.
      </div>
    </div>
  );
}

export function TermsModal({ open, onClose }) {
  const { t: tr } = useI18n();
  if (!open) return null;
  return (
    <div className="modal-backdrop modal-center" onClick={onClose}>
      <div data-testid="terms-modal" onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: R.lg, width: "100%", maxWidth: 640, maxHeight: "88vh", display: "flex", flexDirection: "column", border: `1px solid ${C.borderStrong}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${S.lg}px ${S.lg}px ${S.md}px`, borderBottom: `1px solid ${C.border}` }}>
          <div className="display" style={{ color: C.text, fontSize: 18, fontWeight: 900 }}>{tr("terms.title")}</div>
          <button data-testid="terms-close" className="pressable" onClick={onClose} style={{ background: "transparent", border: "none", display: "flex" }}><Icon name="close" size={26} color={C.text} /></button>
        </div>
        <div style={{ padding: S.lg, overflowY: "auto" }}>
          <TermsContent />
        </div>
        <div style={{ padding: S.md, borderTop: `1px solid ${C.border}` }}>
          <button data-testid="terms-accept-close" className="pressable" onClick={onClose} style={{ width: "100%", background: C.amber, color: C.black, fontWeight: 900, fontSize: 16, border: "none", borderRadius: R.pill, minHeight: 50 }}>{tr("terms.close")}</button>
        </div>
      </div>
    </div>
  );
}
